import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Bell, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { logoutAdmin, getVerificationStats, getAllCoachProfiles, sendNewVerificationNotification } from '../services/firebase';
import DashboardStats from '../components/dashboard/DashboardStats';
import VerificationQueue from '../components/verification/VerificationQueue';
import { VerificationStats } from '../types';
import toast from 'react-hot-toast';

// Import your logo
import cruitLogo from '../assets/logos/cruit-logo.png';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<VerificationStats>({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    recentSubmissions: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
    // Debug: Load all coach profiles to see what's in the database
    debugCoachProfiles();
    
    // Set up real-time listener for new verifications
    const unsubscribe = setupNewVerificationListener();
    
    return () => {
      unsubscribe();
    };
  }, []);

  const loadStats = async () => {
    try {
      const verificationStats = await getVerificationStats();
      setStats(verificationStats);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const debugCoachProfiles = async () => {
    try {
      await getAllCoachProfiles();
    } catch (error) {
      console.error('Error loading debug data:', error);
    }
  };

  const setupNewVerificationListener = () => {
    // This will be implemented to listen for new verifications
    // For now, we'll set up a basic listener that checks for new pending verifications
    const checkForNewVerifications = async () => {
      try {
        const allCoaches = await getAllCoachProfiles();
        const newPendingCoaches = allCoaches.filter(coach => 
          coach.verificationStatus === 'pending' && 
          coach.verificationSubmittedAt && 
          coach.verificationSubmittedAt.toDate && 
          coach.verificationSubmittedAt.toDate() > new Date(Date.now() - 5 * 60 * 1000) // Last 5 minutes
        );
        
        // Send notifications for new verifications
        for (const coach of newPendingCoaches) {
          await sendNewVerificationNotification(coach);
        }
      } catch (error) {
        console.error('Error checking for new verifications:', error);
      }
    };

    // Check every 30 seconds for new verifications
    const interval = setInterval(checkForNewVerifications, 30000);
    
    // Return cleanup function
    return () => {
      clearInterval(interval);
    };
  };

  const handleLogout = async () => {
    try {
      await logoutAdmin();
      navigate('/login');
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-dark"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side - Logo and Brand */}
            <div className="flex items-center space-x-3">
              {/* Logo */}
              <img src={cruitLogo} alt="Cruit Logo" className="h-8" />
            </div>
            
            {/* Right side - User actions */}
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                <Bell className="h-5 w-5" />
              </button>
              <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                <Settings className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">{user?.email}</span>
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <DashboardStats stats={stats} />
        
        {/* Verification Queue */}
        <VerificationQueue />
      </main>
    </div>
  );
};

export default Dashboard; 